// src/pages/account/MyPurchasePage.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Package, Store, MessageCircle, AlertCircle, Truck, Search, X } from "lucide-react";
import Swal from "../../utils/apparifySwal";

import { getImageUrl } from "../../services/api";
import { redirectTo3DS } from "../../services/checkoutService";
import { getOrders, cancelOrder, requestReturnRefund, settleOrderPayment } from "../../services/purchaseService";
import { useMessaging } from "../../context/MessagingContext";
import { useAuth } from "../../context/AuthContext";
import PaginationControls from "../../components/PaginationControls";
import ReturnRefundRequestModal from "../../components/orders/ReturnRefundRequestModal";
import ReturnRefundStatusCard from "../../components/orders/ReturnRefundStatusCard";
import ShopProductNameLink from "../../components/ShopProductNameLink";
import useDebounce from "../../hooks/useDebounce";
import { openShopperBusinessConversation } from "../../utils/shopperMessaging";
import { createClientRequestId } from "../../utils/clientRequestId";
import { persistCheckoutRecoveryContext } from "../../utils/checkoutRecovery";
import { normalizeReturnRequestMedia } from "../../utils/returnRequestMedia";
import { ListSkeleton } from "../../components/skeletons/ShopperSkeletons";

const TABS = [
  { id: "ALL", label: "All" },
  { id: "TO_PAY", label: "To Pay" },
  { id: "TO_SHIP", label: "To Ship" },
  { id: "TO_RECEIVE", label: "To Receive" },
  { id: "COMPLETED", label: "Completed" },
  { id: "CANCELLED", label: "Cancelled" },
  { id: "RETURN_REFUND", label: "Return / Refund" },
];

const PAGE_SIZE = 10;
const CANCELLATION_REASON_OTHER = "Other";
const CANCELLATION_REASON_MAX_LENGTH = 200;
const CANCELLATION_REASON_OPTIONS = [
  "Changed my mind",
  "Ordered by mistake",
  "Found a better price elsewhere",
  "Need to change delivery details",
  "Item no longer needed",
  CANCELLATION_REASON_OTHER,
];
const RECENT_REVIEWED_ORDER_ITEMS_KEY = "shopper_recent_reviewed_order_items";
const ORDER_SEARCH_PARAM = "orderSearch";

function getPurchasePaymentReturnMessage(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "paid") {
    return "Payment confirmed. Your order has been updated in My Purchase.";
  }

  if (normalized === "cancelled") {
    return "PayMongo checkout was cancelled. You can settle this order again from My Purchase.";
  }

  if (normalized === "failed") {
    return "PayMongo checkout did not complete. You can settle this order again from My Purchase.";
  }

  if (normalized === "expired" || normalized === "expired_terminal") {
    return "The PayMongo payment window expired. Review the latest order status from My Purchase.";
  }

  return "";
}

function normalizeOrderSearchValue(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeOrderItemId(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function isRecentlyReviewedOrderItem(orderItemId) {
  const normalizedId = normalizeOrderItemId(orderItemId);
  if (!normalizedId) return false;

  try {
    const raw = sessionStorage.getItem(RECENT_REVIEWED_ORDER_ITEMS_KEY);
    const parsed = JSON.parse(raw || "[]");
    if (!Array.isArray(parsed)) return false;
    return parsed.some((value) => Number(value) === normalizedId);
  } catch {
    return false;
  }
}

function formatAmount(amount) {
  return (
    "₱" +
    Number(amount || 0).toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatDate(value) {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function normalizeCheckoutPayment(checkoutPayment) {
  if (!checkoutPayment) return null;

  return {
    state: String(checkoutPayment?.state || "").trim().toUpperCase() || null,
    label: checkoutPayment?.label || null,
    message: checkoutPayment?.message || "",
    canSettle: Boolean(checkoutPayment?.canSettle ?? checkoutPayment?.can_settle),
    expiresAt: checkoutPayment?.expiresAt || checkoutPayment?.expires_at || null,
    sessionStatus: checkoutPayment?.sessionStatus || checkoutPayment?.session_status || null,
    latestPaymentStatus:
      checkoutPayment?.latestPaymentStatus || checkoutPayment?.latest_payment_status || null,
    checkoutSessionId:
      checkoutPayment?.checkoutSessionId || checkoutPayment?.checkout_session_id || null,
  };
}

function getStatusInfo(order) {
  const orderStatus = String(order?.order_status || "").trim().toUpperCase();
  const paymentMethod = String(order?.payment_method || "").trim().toUpperCase();
  const paymentStatus = String(order?.payment_status || "").trim().toUpperCase();
  const checkoutPaymentState = String(order?.checkoutPayment?.state || "").trim().toUpperCase();

  if (checkoutPaymentState === "AWAITING_PAYMENT") {
    return {
      label: "Awaiting Payment",
      cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    };
  }

  if (checkoutPaymentState === "EXPIRED") {
    return {
      label: "Payment Expired",
      cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    };
  }

  if (checkoutPaymentState === "FAILED") {
    return {
      label: "Payment Failed",
      cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    };
  }

  if (orderStatus === "PENDING" && paymentMethod !== "COD" && paymentStatus === "PENDING") {
    return {
      label: "Awaiting Payment",
      cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    };
  }

  if (orderStatus === "PENDING" && paymentMethod === "COD") {
    return {
      label: "To Ship",
      cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    };
  }

  switch (orderStatus) {
    case "TO_SHIP":
      return {
        label: "To Ship",
        cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      };
    case "TO_RECEIVE":
      return {
        label: "To Receive",
        cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
      };
    case "COMPLETED":
      return {
        label: "Completed",
        cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
      };
    case "RETURN_REFUND":
      return {
        label: "Return / Refund",
        cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
      };
    case "PAID":
      return {
        label: "To Ship",
        cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      };
    default:
      return {
        label: orderStatus || "Processing",
        cls: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
      };
  }
}

function getTabLabel(tabId) {
  return TABS.find((tab) => tab.id === tabId)?.label || "All";
}

function getEmptyMessage(tabId, searchQuery = "") {
  const normalizedSearchQuery = String(searchQuery || "").trim();

  if (normalizedSearchQuery) {
    const scopeLabel = tabId === "ALL" ? "past orders" : `${getTabLabel(tabId).toLowerCase()} orders`;
    return `No ${scopeLabel} matched "${normalizedSearchQuery}"`;
  }

  switch (tabId) {
    case "TO_PAY":
      return "No pending online payments";
    case "TO_SHIP":
      return "No orders awaiting shipment";
    case "TO_RECEIVE":
      return "No orders currently on the way";
    case "COMPLETED":
      return "No completed orders yet";
    case "CANCELLED":
      return "No cancelled orders";
    case "RETURN_REFUND":
      return "No return or refund orders";
    default:
      return "You have no orders yet";
  }
}

function isOrderCancellable(order) {
  const normalizedStatus = String(order?.order_status || "")
    .trim()
    .toUpperCase();
  const normalizedPaymentMethod = String(order?.payment_method || "")
    .trim()
    .toUpperCase();

  return (
    normalizedStatus === "TO_SHIP" ||
    normalizedStatus === "PAID" ||
    (normalizedStatus === "PENDING" && normalizedPaymentMethod === "COD")
  );
}

function isOrderDelivered(order) {
  const normalizedStatus = String(order?.order_status || order?.status || "")
    .trim()
    .toUpperCase();

  return normalizedStatus === "COMPLETED" || normalizedStatus === "DELIVERED";
}

function isSubOrderDelivered(subOrder) {
  const normalizedStatus = String(subOrder?.order_status || subOrder?.status || "")
    .trim()
    .toUpperCase();

  return normalizedStatus === "COMPLETED" || normalizedStatus === "DELIVERED";
}

function hasDeliveredReviewWindow(order) {
  if (isOrderDelivered(order)) return true;
  if (!Array.isArray(order?.sub_orders) || order.sub_orders.length === 0) return false;
  return order.sub_orders.some((subOrder) => isSubOrderDelivered(subOrder));
}

function getDeliveredReviewItems(order) {
  if (!order) return [];

  if (Array.isArray(order?.sub_orders) && order.sub_orders.length > 0) {
    return order.sub_orders
      .filter((subOrder) => isSubOrderDelivered(subOrder))
      .flatMap((subOrder) => subOrder.items || []);
  }

  if (isOrderDelivered(order)) {
    return order.items || [];
  }

  return [];
}

function getDisplayCancellationReason(reason) {
  const rawReason = String(reason || "").trim();
  if (!rawReason) return "";
  return rawReason.replace(/^shopper:\s*/i, "").trim();
}

function normalizeReturnRequest(returnRequest) {
  if (!returnRequest) return null;

  const evidence = normalizeReturnRequestMedia(
    returnRequest?.evidence || returnRequest?.media,
    returnRequest?.meta?.evidence || returnRequest?.meta?.media
  );

  return {
    id: returnRequest?.id ?? null,
    orderId: returnRequest?.order_id ?? returnRequest?.orderId ?? null,
    status: returnRequest?.request_status || returnRequest?.requestStatus || returnRequest?.status || null,
    reason: returnRequest?.reason || null,
    details: returnRequest?.details || null,
    refundAmount:
      returnRequest?.refundAmount != null
        ? Number(returnRequest.refundAmount)
        : returnRequest?.refund_amount != null
          ? Number(returnRequest.refund_amount)
          : null,
    refundDestination: returnRequest?.refundDestination || returnRequest?.refund_destination || null,
    walletTransactionId: returnRequest?.walletTransactionId || returnRequest?.wallet_transaction_id || null,
    evidence,
    media: evidence,
    requestedAt: returnRequest?.requested_at || returnRequest?.requestedAt || returnRequest?.created_at || null,
    reviewedAt: returnRequest?.reviewed_at || returnRequest?.reviewedAt || null,
    refundedAt: returnRequest?.refundedAt || returnRequest?.refunded_at || null,
    resolutionNote: returnRequest?.resolution_note || returnRequest?.resolutionNote || null,
    meta: returnRequest?.meta || returnRequest?.meta_json || null,
    policy: returnRequest?.policy || returnRequest?.meta?.policy || returnRequest?.meta_json?.policy || null,
  };
}

function normalizeReturnRefund(returnRefund) {
  if (!returnRefund) return null;

  const evidence = normalizeReturnRequestMedia(
    returnRefund?.evidence || returnRefund?.media,
    returnRefund?.meta?.evidence || returnRefund?.meta?.media
  );

  return {
    eligible: Boolean(returnRefund?.eligible),
    requested: Boolean(returnRefund?.requested),
    status: returnRefund?.status || null,
    reason: returnRefund?.reason || null,
    details: returnRefund?.details || null,
    refundAmount:
      returnRefund?.refundAmount != null
        ? Number(returnRefund.refundAmount)
        : returnRefund?.refund_amount != null
          ? Number(returnRefund.refund_amount)
          : null,
    refundDestination: returnRefund?.refundDestination || returnRefund?.refund_destination || null,
    walletTransactionId: returnRefund?.walletTransactionId || returnRefund?.wallet_transaction_id || null,
    evidence,
    media: evidence,
    requestedAt: returnRefund?.requested_at || returnRefund?.requestedAt || null,
    reviewedAt: returnRefund?.reviewed_at || returnRefund?.reviewedAt || null,
    refundedAt: returnRefund?.refundedAt || returnRefund?.refunded_at || null,
    resolutionNote: returnRefund?.resolution_note || returnRefund?.resolutionNote || null,
    deliveredAt: returnRefund?.delivered_at || returnRefund?.deliveredAt || null,
    deadlineAt: returnRefund?.deadline_at || returnRefund?.deadlineAt || null,
    daysRemaining:
      returnRefund?.days_remaining != null
        ? Number(returnRefund.days_remaining)
        : returnRefund?.daysRemaining != null
          ? Number(returnRefund.daysRemaining)
          : null,
    message: returnRefund?.message || "",
    meta: returnRefund?.meta || returnRefund?.meta_json || null,
    policy: returnRefund?.policy || returnRefund?.meta?.policy || returnRefund?.meta_json?.policy || null,
  };
}

function getItemProductKey(item) {
  const raw = item?.product_id ?? item?.productId ?? null;
  if (raw === null || typeof raw === "undefined") return null;
  const normalized = String(raw).trim();
  return normalized || null;
}

function normalizeShopperReview(review) {
  if (!review) return null;

  return {
    id: review?.id ?? null,
    rating: Number(review?.rating || 0),
    title: review?.title || null,
    reviewText: review?.reviewText || review?.review_text || null,
    status: review?.status || review?.review_status || null,
    recommended: review?.recommended == null ? null : Boolean(review.recommended),
    photos: Array.isArray(review?.photos) ? review.photos : [],
    createdAt: review?.createdAt || review?.created_at || null,
  };
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return [];

  return items.map((item, index) => ({
    id: item?.id ?? `${getItemProductKey(item) || "item"}-${index}`,
    order_id: item?.order_id ?? item?.orderId ?? null,
    orderId: item?.order_id ?? item?.orderId ?? null,
    product_id: getItemProductKey(item),
    productId: getItemProductKey(item),
    variant_id: item?.variant_id ?? null,
    variantId: item?.variant_id ?? null,
    name: item?.name || "Product",
    image: item?.image || null,
    quantity: Number(item?.quantity || 0),
    price: Number(item?.price || 0),
    variation: item?.variation || null,
    shopperReview: normalizeShopperReview(item?.shopperReview || item?.shopper_review),
    shopper_review: normalizeShopperReview(item?.shopperReview || item?.shopper_review),
  }));
}

function normalizeSubOrders(subOrders) {
  if (!Array.isArray(subOrders)) return [];

  return subOrders.map((subOrder, index) => ({
    id: subOrder?.id ?? `sub-${index}`,
    business_id: subOrder?.business_id ?? null,
    business_name: subOrder?.business_name || "Seller",
    businessReturnAddress: subOrder?.businessReturnAddress || subOrder?.business_return_address || null,
    business_return_address: subOrder?.businessReturnAddress || subOrder?.business_return_address || null,
    orderReference: subOrder?.orderReference || subOrder?.order_reference || null,
    status: subOrder?.status || null,
    order_status: subOrder?.order_status || null,
    shipping_method: subOrder?.shipping_method || null,
    customer_note: subOrder?.customer_note || null,
    delivery_fee: Number(subOrder?.delivery_fee || 0),
    shipment: subOrder?.shipment || null,
    returnRequest: normalizeReturnRequest(subOrder?.return_request || subOrder?.returnRequest),
    returnRefund: normalizeReturnRefund(subOrder?.return_refund || subOrder?.returnRefund),
    items: normalizeItems(subOrder?.items),
  }));
}

function normalizeOrder(order) {
  const normalizedSubOrders = normalizeSubOrders(order?.sub_orders);
  const normalizedItems = normalizeItems(order?.items);
  const checkoutPayment = normalizeCheckoutPayment(order?.checkout_payment || order?.checkoutPayment);

  const fallbackFlatItems =
    normalizedSubOrders.length > 0
      ? normalizedSubOrders.flatMap((subOrder) => subOrder.items)
      : normalizedItems;

  return {
    id: order?.id ?? null,
    reference: order?.order_reference || order?.orderReference || null,
    status: order?.status || null,
    order_status: order?.order_status || null,
    payment_status: order?.payment_status || null,
    payment_method: order?.payment_method || null,
    cancellationReason: order?.cancellation_reason || order?.cancellationReason || null,
    businessReturnAddress: order?.businessReturnAddress || order?.business_return_address || null,
    business_return_address: order?.businessReturnAddress || order?.business_return_address || null,
    returnRequest: normalizeReturnRequest(order?.return_request || order?.returnRequest),
    returnRefund: normalizeReturnRefund(order?.return_refund || order?.returnRefund),
    total_amount: Number(order?.total_amount || 0),
    delivery_fee: Number(order?.delivery_fee || 0),
    created_at: order?.created_at || null,
    paid_at: order?.paid_at || null,
    checkoutPayment,
    sub_orders: normalizedSubOrders,
    items: fallbackFlatItems,
  };
}

function toSettlePaymentErrorMessage(err) {
  const rawMessage = String(err?.message || "").trim();

  if (
    rawMessage.includes("CHECKOUT_PAYMENT_NOT_SETTLEABLE")
    || rawMessage.includes("This order can no longer be settled")
    || rawMessage.includes("ORDER_INVALID")
  ) {
    return "This PayMongo payment window already closed. Refresh My Purchase to see the updated order status.";
  }

  if (rawMessage.includes("CHECKOUT_REQUEST_LOCK_TIMEOUT")) {
    return "Your payment session is still being prepared. Please wait a moment, then try again.";
  }

  if (rawMessage.includes("PAYMONGO_CHECKOUT_URLS_NOT_CONFIGURED") || rawMessage.includes("PAYMENT_PROVIDER_NOT_CONFIGURED")) {
    return "Online payment is temporarily unavailable. Please try again later.";
  }

  if (rawMessage.startsWith("Request failed:")) {
    return rawMessage.replace(/^Request failed:\s*\d+\s*—\s*/i, "");
  }

  return rawMessage || "Unable to continue PayMongo checkout right now.";
}

function matchesOrderSearchText(value, normalizedQuery) {
  if (!normalizedQuery) return true;
  return normalizeOrderSearchValue(value).toLowerCase().includes(normalizedQuery);
}

function getMatchingOrderItems(items, normalizedQuery) {
  const safeItems = Array.isArray(items) ? items : [];
  if (!normalizedQuery) return safeItems;

  return safeItems.filter((item) => (
    matchesOrderSearchText(item?.name, normalizedQuery)
    || matchesOrderSearchText(item?.variation, normalizedQuery)
  ));
}

function getVisibleOrderForQuery(order, searchQuery) {
  const normalizedQuery = normalizeOrderSearchValue(searchQuery).toLowerCase();
  if (!normalizedQuery) return order;
  if (!order) return null;

  const rootReferenceMatches = matchesOrderSearchText(order?.reference, normalizedQuery);
  const hasSubOrders = Array.isArray(order?.sub_orders) && order.sub_orders.length > 0;

  if (!hasSubOrders) {
    const visibleItems = rootReferenceMatches
      ? (Array.isArray(order?.items) ? order.items : [])
      : getMatchingOrderItems(order?.items, normalizedQuery);

    if (!rootReferenceMatches && visibleItems.length === 0) return null;

    return {
      ...order,
      items: visibleItems,
    };
  }

  const visibleSubOrders = order.sub_orders
    .map((subOrder) => {
      const subOrderReferenceMatches = matchesOrderSearchText(subOrder?.orderReference, normalizedQuery);
      const businessMatches = matchesOrderSearchText(subOrder?.business_name, normalizedQuery);

      if (rootReferenceMatches || subOrderReferenceMatches || businessMatches) {
        return {
          ...subOrder,
          items: Array.isArray(subOrder?.items) ? subOrder.items : [],
        };
      }

      const visibleItems = getMatchingOrderItems(subOrder?.items, normalizedQuery);
      if (visibleItems.length === 0) return null;

      return {
        ...subOrder,
        items: visibleItems,
      };
    })
    .filter(Boolean);

  if (visibleSubOrders.length === 0) return null;

  return {
    ...order,
    sub_orders: visibleSubOrders,
    items: visibleSubOrders.flatMap((subOrder) => (Array.isArray(subOrder.items) ? subOrder.items : [])),
  };
}

function OrderItemRow({ item }) {
  const imageUrl = getImageUrl(item.image);

  return (
    <div className="flex gap-3 py-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800 sm:h-[72px] sm:w-[72px]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package size={20} className="text-neutral-300 dark:text-neutral-600" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <ShopProductNameLink
          product={item}
          className="block text-sm font-normal leading-snug line-clamp-2"
          fallbackClassName="block line-clamp-2 text-sm font-medium leading-snug text-neutral-900 dark:text-neutral-100"
        >
          {item.name}
        </ShopProductNameLink>

        {item.variation ? (
          <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">
            {item.variation}
          </p>
        ) : null}

        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          x{item.quantity}
        </p>
      </div>

      <div className="shrink-0 text-sm font-medium text-neutral-900 dark:text-neutral-100">
        {formatAmount(item.price * item.quantity)}
      </div>
    </div>
  );
}

function SellerSection({
  subOrder,
  shopperOrderReference,
  onContactSeller,
  onRequestReturnRefund,
  requestingReturnRefundOrderId,
}) {
  const returnRefund = subOrder?.returnRefund || null;
  const returnRequest = subOrder?.returnRequest || null;
  const hasRequestedReturnRefund = Boolean(returnRefund?.requested || returnRequest);
  const canRequestReturnRefund = Boolean(returnRefund?.eligible);
  const isRequestingReturnRefund = Number(requestingReturnRefundOrderId) === Number(subOrder?.id);
  const sellerNote = String(subOrder?.customer_note || "").trim();

  return (
    <div>
      <div className="flex items-center justify-between gap-3 border-t border-neutral-100 bg-neutral-50 px-4 py-2.5 dark:border-neutral-800 dark:bg-neutral-800/40">
        <div className="flex min-w-0 items-center gap-2">
          <Store size={13} className="shrink-0 text-indigo-600 dark:text-indigo-400" />
          <span className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {subOrder.business_name}
          </span>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canRequestReturnRefund ? (
            <button
              type="button"
              onClick={() => onRequestReturnRefund({ ...subOrder, shopperOrderReference })}
              disabled={isRequestingReturnRefund}
              className="inline-flex shrink-0 items-center rounded-full border border-orange-300 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-900/20"
            >
              {isRequestingReturnRefund ? "Submitting..." : "Return / Refund"}
            </button>
          ) : null}

          {subOrder.business_id ? (
            <button
              type="button"
              onClick={() => onContactSeller(subOrder.business_id)}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 transition hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <MessageCircle size={12} />
              Chat
            </button>
          ) : null}
        </div>
      </div>

      {hasRequestedReturnRefund ? (
        <ReturnRefundStatusCard
          returnRefund={returnRefund}
          returnRequest={returnRequest}
          orderReference={shopperOrderReference || null}
          returnAddress={subOrder?.businessReturnAddress || subOrder?.business_return_address || null}
          formatAmount={formatAmount}
          formatDateTime={formatDate}
          collapsible
          defaultExpanded={false}
          className="mx-4 mt-3"
        />
      ) : null}

      {sellerNote ? (
        <div className="mx-4 mt-3 rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-2.5 text-xs text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-100">
          <p className="font-semibold uppercase tracking-[0.16em] text-sky-700 dark:text-sky-200">
            Message to Seller
          </p>
          <p className="mt-1 whitespace-pre-wrap leading-relaxed text-sky-800/90 dark:text-sky-100/90">
            {sellerNote}
          </p>
        </div>
      ) : null}

      <div className="divide-y divide-neutral-100 px-4 dark:divide-neutral-800">
        {subOrder.items.map((item) => (
          <OrderItemRow
            key={item.id}
            item={item}
          />
        ))}
      </div>
    </div>
  );
}

function OrderCard({
  order,
  onContactSeller,
  onRateProduct,
  onTrackOrder,
  onCancelOrder,
  onSettlePayment,
  onRequestReturnRefund,
  cancellingOrderId,
  settlingOrderId,
  requestingReturnRefundOrderId,
}) {
  const status = getStatusInfo(order);
  const hasSubOrders = order.sub_orders.length > 0;
  const showRateButtons = hasDeliveredReviewWindow(order);
  const canCancel = isOrderCancellable(order);
  const checkoutPayment = order.checkoutPayment || null;
  const canSettlePayment = Boolean(checkoutPayment?.canSettle);
  const isCancelling = Number(cancellingOrderId) === Number(order.id);
  const isSettlingPayment = Number(settlingOrderId) === Number(order.id);
  const cancellationReason = getDisplayCancellationReason(order.cancellationReason);
  const orderReturnRefund = order.returnRefund || null;
  const orderReturnRequest = order.returnRequest || null;
  const orderItemsForReviewActions = getDeliveredReviewItems(order);
  const reviewActions = showRateButtons
    ? orderItemsForReviewActions
      .map((item) => {
        const productId = getItemProductKey(item);
        const orderItemId = item?.id ?? null;
        const shopperReview = item?.shopperReview || item?.shopper_review || null;
        const reviewed = Boolean(shopperReview) || isRecentlyReviewedOrderItem(orderItemId);

        if (!productId || !orderItemId) return null;

        return {
          orderItemId,
          orderId: item?.order_id ?? item?.orderId ?? order.id,
          productId,
          productName: item.name || "Product",
          reviewed,
          canRate: !reviewed,
        };
      })
      .filter(Boolean)
    : [];

  return (
    <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex flex-col items-start justify-between gap-2 border-b border-neutral-100 px-4 py-3 dark:border-neutral-800 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <span className="block text-xs font-semibold tracking-[0.12em] text-neutral-700 dark:text-neutral-200">
            {order.reference || "Reference pending"}
          </span>

          {order.created_at ? (
            <span className="block text-xs text-neutral-400 dark:text-neutral-500 sm:inline sm:ml-2">
              <span className="hidden sm:inline">· </span>
              {formatDate(order.created_at)}
            </span>
          ) : null}
        </div>

        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${status.cls}`}>
          {status.label}
        </span>
      </div>

      {hasSubOrders ? (
        order.sub_orders.map((subOrder) => (
          <SellerSection
            key={subOrder.id || `${order.id}-${subOrder.business_id || "seller"}`}
            subOrder={subOrder}
            shopperOrderReference={order?.reference}
            onContactSeller={onContactSeller}
            onRequestReturnRefund={onRequestReturnRefund}
            requestingReturnRefundOrderId={requestingReturnRefundOrderId}
          />
        ))
      ) : (
        <div>
          <div className="flex items-center gap-2 border-t border-neutral-100 bg-neutral-50 px-4 py-2.5 dark:border-neutral-800 dark:bg-neutral-800/40">
            <Store size={13} className="shrink-0 text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Seller
            </span>
          </div>

          <div className="divide-y divide-neutral-100 px-4 dark:divide-neutral-800">
            {order.items.map((item) => (
              <OrderItemRow
                key={item.id}
                item={item}
              />
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-neutral-100 px-4 py-3 dark:border-neutral-800">
        {checkoutPayment?.message ? (
          <div
            className={[
              "mb-3 rounded-xl border px-3 py-2 text-xs",
              checkoutPayment.state === "AWAITING_PAYMENT"
                ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
                : checkoutPayment.state === "EXPIRED"
                  ? "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200"
                  : checkoutPayment.state === "FAILED"
                    ? "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200"
                    : "border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200",
            ].join(" ")}
          >
            <span className="font-semibold">{checkoutPayment.label || "Payment"}:</span> {checkoutPayment.message}
            {checkoutPayment.expiresAt ? (
              <span className="block pt-1 font-medium">
                {checkoutPayment.state === "EXPIRED" ? "Expired" : "Window closes"} {formatDateTime(checkoutPayment.expiresAt)}
              </span>
            ) : null}
          </div>
        ) : null}

        {order.order_status === "CANCELLED" && cancellationReason ? (
          <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
            <span className="font-semibold">Cancellation Reason:</span> {cancellationReason}
          </div>
        ) : null}

        {!hasSubOrders && (orderReturnRefund?.requested || orderReturnRequest) ? (
          <ReturnRefundStatusCard
            returnRefund={orderReturnRefund}
            returnRequest={orderReturnRequest}
            orderReference={order?.reference}
            returnAddress={order?.businessReturnAddress || order?.business_return_address || null}
            formatAmount={formatAmount}
            formatDateTime={formatDate}
            collapsible
            defaultExpanded={false}
            className="mb-3"
          />
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
              <span>Shipping Fee</span>
              <span className="font-semibold text-neutral-700 dark:text-neutral-300">
                {formatAmount(order.delivery_fee)}
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Order Total
              </span>
              <span className="text-base font-bold text-neutral-900 dark:text-neutral-100">
                {formatAmount(order.total_amount)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canSettlePayment ? (
              <button
                type="button"
                onClick={() => onSettlePayment(order)}
                disabled={isSettlingPayment}
                className="rounded-xl bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                {isSettlingPayment ? "Opening Payment..." : "Settle Payment"}
              </button>
            ) : null}

            {canCancel ? (
              <button
                type="button"
                onClick={() => onCancelOrder(order)}
                disabled={isCancelling}
                className="rounded-xl border border-rose-300 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800/60 dark:text-rose-300 dark:hover:bg-rose-900/20"
              >
                {isCancelling ? "Cancelling..." : "Cancel Order"}
              </button>
            ) : null}

            {reviewActions
              .filter((action) => action.canRate)
              .map((action) => (
                <button
                  key={`rate-${action.orderItemId}`}
                  type="button"
                  onClick={() => onRateProduct(action)}
                  title={action.productName}
                  aria-label={`Rate ${action.productName}`}
                  className="inline-flex items-center rounded-xl border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
                >
                  Rate
                </button>
              ))}

            {reviewActions
              .filter((action) => action.reviewed)
              .map((action) => (
                <span
                  key={`reviewed-${action.orderItemId}`}
                  title={action.productName}
                  className="inline-flex items-center rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300"
                >
                  Reviewed
                </span>
              ))}

            {!hasSubOrders && orderReturnRefund?.eligible ? (
              <button
                type="button"
                onClick={() => onRequestReturnRefund(order)}
                disabled={Number(requestingReturnRefundOrderId) === Number(order.id)}
                className="rounded-xl border border-orange-300 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-900/20"
              >
                {Number(requestingReturnRefundOrderId) === Number(order.id) ? "Submitting..." : "Return / Refund"}
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => onTrackOrder(order.id)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <Truck size={13} />
              Track Order
            </button>

          </div>
        </div>
      </div>
    </article>
  );
}

export default function MyPurchasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  const { startConversation } = useMessaging();

  const visibleOrdersRef = useRef(0);
  const lastSuccessQueryRef = useRef("");
  const orderSearchInputRef = useRef(null);
  const pendingOrderSearchParamRef = useRef(null);

  const [activeTab, setActiveTab] = useState("ALL");
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [settlingOrderId, setSettlingOrderId] = useState(null);
  const [requestingReturnRefundOrderId, setRequestingReturnRefundOrderId] = useState(null);
  const [returnRefundDialogOrder, setReturnRefundDialogOrder] = useState(null);
  const [cancelDialogOrder, setCancelDialogOrder] = useState(null);
  const [selectedCancellationReason, setSelectedCancellationReason] = useState("");
  const [customCancellationReason, setCustomCancellationReason] = useState("");
  const [cancelDialogError, setCancelDialogError] = useState("");

  const orderSearchQuery = searchParams.get(ORDER_SEARCH_PARAM) || "";
  const normalizedOrderSearchQuery = normalizeOrderSearchValue(orderSearchQuery);
  const [orderSearchInput, setOrderSearchInput] = useState(normalizedOrderSearchQuery);
  const [appliedOrderSearchQuery, setAppliedOrderSearchQuery] = useState(normalizedOrderSearchQuery);
  const previousAppliedOrderSearchQueryRef = useRef(normalizedOrderSearchQuery);
  const debouncedOrderSearchInput = useDebounce(orderSearchInput, 300);

  const syncOrderSearchParam = useCallback(
    (nextValue) => {
      const normalizedValue = normalizeOrderSearchValue(nextValue);
      setSearchParams((currentParams) => {
        const currentValue = normalizeOrderSearchValue(currentParams.get(ORDER_SEARCH_PARAM) || "");
        if (currentValue === normalizedValue) {
          pendingOrderSearchParamRef.current = null;
          return currentParams;
        }

        pendingOrderSearchParamRef.current = normalizedValue;

        const nextParams = new URLSearchParams(currentParams);

        if (normalizedValue) {
          nextParams.set(ORDER_SEARCH_PARAM, normalizedValue);
        } else {
          nextParams.delete(ORDER_SEARCH_PARAM);
        }

        return nextParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (pendingOrderSearchParamRef.current === normalizedOrderSearchQuery) {
      pendingOrderSearchParamRef.current = null;
      return;
    }

    pendingOrderSearchParamRef.current = null;
    setOrderSearchInput((currentValue) => (
      currentValue === normalizedOrderSearchQuery ? currentValue : normalizedOrderSearchQuery
    ));
    setAppliedOrderSearchQuery((currentValue) => (
      currentValue === normalizedOrderSearchQuery ? currentValue : normalizedOrderSearchQuery
    ));
  }, [normalizedOrderSearchQuery]);

  useEffect(() => {
    const normalizedInput = normalizeOrderSearchValue(debouncedOrderSearchInput);
    setAppliedOrderSearchQuery((currentValue) => (
      currentValue === normalizedInput ? currentValue : normalizedInput
    ));
  }, [debouncedOrderSearchInput]);

  useEffect(() => {
    syncOrderSearchParam(appliedOrderSearchQuery);
  }, [appliedOrderSearchQuery, syncOrderSearchParam]);

  const fetchOrders = useCallback(
    async (tab, currentPage, query, signal) => {
      const normalizedQuery = String(query || "").replace(/\s+/g, " ").trim();
      const queryKey = `${tab}:${currentPage}:${normalizedQuery.toLowerCase()}`;
      const shouldRefreshInPlace = visibleOrdersRef.current > 0;

      if (shouldRefreshInPlace) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const result = await getOrders({
          status: tab,
          page: currentPage,
          limit: PAGE_SIZE,
          q: normalizedQuery || undefined,
          signal,
        });

        const nextMeta = result?.meta || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 };
        const normalizedOrders = Array.isArray(result?.items)
          ? result.items.map(normalizeOrder)
          : [];

        if (
          normalizedOrders.length === 0 &&
          Number(nextMeta.total || 0) > 0 &&
          Number(currentPage) > Number(nextMeta.totalPages || 1)
        ) {
          setPage(Number(nextMeta.totalPages || 1));
          return;
        }

        setOrders(normalizedOrders);
        setMeta(nextMeta);
        setActionError("");
        visibleOrdersRef.current = normalizedOrders.length;
        lastSuccessQueryRef.current = queryKey;
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || err?.isCanceled) {
          return;
        }

        if (err?.status === 401 || err?.statusCode === 401 || err?.status === 403 || err?.statusCode === 403) {
          logout();
          navigate("/login", {
            replace: true,
            state: { from: "/account/purchase" },
          });
          return;
        }

        console.error("[MyPurchasePage] fetchOrders failed", err);

        if (shouldRefreshInPlace && lastSuccessQueryRef.current === queryKey) {
          setActionError("Unable to refresh your orders right now.");
        } else {
          setError("Unable to load orders. Please try again.");
          setOrders([]);
          setMeta({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
          visibleOrdersRef.current = 0;
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [logout, navigate]
  );

  useEffect(() => {
    if (previousAppliedOrderSearchQueryRef.current !== appliedOrderSearchQuery) {
      previousAppliedOrderSearchQueryRef.current = appliedOrderSearchQuery;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }

    const controller = new AbortController();
    fetchOrders(activeTab, page, appliedOrderSearchQuery, controller.signal);

    return () => {
      controller.abort();
    };
  }, [activeTab, page, fetchOrders, appliedOrderSearchQuery]);

  useEffect(() => {
    if (!actionError) return undefined;

    const timeoutId = window.setTimeout(() => {
      setActionError("");
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [actionError]);

  useEffect(() => {
    const message = getPurchasePaymentReturnMessage(location.state?.paymentReturnStatus);
    if (!message) return;

    setActionError(message);
  }, [location.state]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActionError("");
    setActiveTab(tabId);
    setPage(1);
  };

  const handleSubmitOrderSearch = useCallback(() => {
    const normalizedValue = normalizeOrderSearchValue(orderSearchInput);
    setOrderSearchInput(normalizedValue);
    setAppliedOrderSearchQuery(normalizedValue);
  }, [orderSearchInput]);

  const handleClearOrderSearch = useCallback(() => {
    setOrderSearchInput("");
    setAppliedOrderSearchQuery("");
    orderSearchInputRef.current?.focus();
  }, []);

  const handleContactSeller = async (businessId) => {
    if (!businessId) return;

    try {
      const conversation = await openShopperBusinessConversation({
        businessId,
        navigate,
        startConversation,
      });

      if (conversation?.id) return;

      setActionError("Unable to open this seller conversation right now.");
    } catch (err) {
      console.error("[MyPurchasePage] startConversation failed", err);
      setActionError("Unable to open this seller conversation right now.");
    }
  };

  const handleRateProduct = async (reviewAction) => {
    setActionError("");
    if (!reviewAction?.productId || !reviewAction?.orderItemId) {
      setActionError("This item is not ready for review yet.");
      return;
    }

    navigate(`/product/${reviewAction.productId}#product-reviews-section`, {
      state: {
        openWriteReview: true,
        from: "/account/purchase",
        reviewContext: {
          productId: reviewAction.productId,
          productName: reviewAction.productName || null,
          orderId: reviewAction.orderId || null,
          orderItemId: reviewAction.orderItemId,
        },
      },
    });
  };

  const handleTrackOrder = (orderId) => {
    if (!orderId) return;
    navigate(`/account/purchase/${orderId}/tracking`);
  };

  const handleSettlePayment = async (order) => {
    const orderId = order?.id;
    if (!orderId) return;

    setActionError("");
    setSettlingOrderId(orderId);

    try {
      const clientRequestId = createClientRequestId();
      const session = await settleOrderPayment(orderId, {
        clientRequestId,
        allowSessionRenewal: true,
        returnFlow: "order_settlement",
      });

      if (!session?.checkoutUrl) {
        if (String(session?.paymentStatus || "").toUpperCase() === "PAID") {
          await fetchOrders(activeTab, page, appliedOrderSearchQuery);
          navigate(`/order-confirmation/${session.orderId || orderId}`);
          return;
        }

        throw new Error("Unable to continue PayMongo checkout right now.");
      }

      const recoveryContext = {
        orderId: session.orderId || orderId,
        checkoutSessionId: session.checkoutSessionId || null,
        clientRequestId,
        createdAt: new Date().toISOString(),
        flow: "order_settlement",
        returnPath: `${location.pathname}${location.search || ""}` || "/account/purchase",
      };

      persistCheckoutRecoveryContext(recoveryContext);
      redirectTo3DS(session.checkoutUrl);
    } catch (err) {
      if (err?.status === 401 || err?.statusCode === 401 || err?.status === 403 || err?.statusCode === 403) {
        logout();
        navigate("/login", {
          replace: true,
          state: { from: "/account/purchase" },
        });
        return;
      }

      const shouldRefreshOrderState = Number(err?.status || err?.statusCode || 0) === 409
        || /CHECKOUT_PAYMENT_NOT_SETTLEABLE|order can no longer be settled/i.test(String(err?.message || ""));

      if (shouldRefreshOrderState) {
        await fetchOrders(activeTab, page, appliedOrderSearchQuery);
      }

      setActionError(toSettlePaymentErrorMessage(err));
    } finally {
      setSettlingOrderId(null);
    }
  };

  const handleRequestReturnRefund = async (targetOrder) => {
    if (!targetOrder?.id) return;
    setActionError("");
    setReturnRefundDialogOrder(targetOrder);
  };

  const handleCloseReturnRefundDialog = () => {
    if (requestingReturnRefundOrderId) return;
    setReturnRefundDialogOrder(null);
  };

  const handleSubmitReturnRefund = async (payload) => {
    const targetOrderId = returnRefundDialogOrder?.id;
    if (!targetOrderId) {
      throw new Error("Unable to identify this seller order.");
    }

    setActionError("");
    setRequestingReturnRefundOrderId(targetOrderId);

    try {
      await requestReturnRefund(targetOrderId, payload);
      await fetchOrders(activeTab, page, appliedOrderSearchQuery);
      setReturnRefundDialogOrder(null);
      await Swal.fire({
        icon: "success",
        title: "Request Submitted",
        text: "Your return / refund request was sent to the seller.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      if (err?.status === 401 || err?.statusCode === 401 || err?.status === 403 || err?.statusCode === 403) {
        logout();
        navigate("/login", {
          replace: true,
          state: { from: "/account/purchase" },
        });
        throw err;
      }

      const message = err?.message || "Unable to submit your return / refund request right now.";
      setActionError(message);
      throw new Error(message);
    } finally {
      setRequestingReturnRefundOrderId(null);
    }
  };

  const handleRequestCancelOrder = (order) => {
    if (!order?.id) return;

    setCancelDialogOrder(order);
    setSelectedCancellationReason("");
    setCustomCancellationReason("");
    setCancelDialogError("");
  };

  const handleCloseCancelDialog = ({ force = false } = {}) => {
    if (cancellingOrderId && !force) return;
    setCancelDialogOrder(null);
    setSelectedCancellationReason("");
    setCustomCancellationReason("");
    setCancelDialogError("");
  };

  const resolveCancellationReason = () => {
    const normalizedSelection = String(selectedCancellationReason || "").trim();
    if (!normalizedSelection) return "";

    if (normalizedSelection !== CANCELLATION_REASON_OTHER) {
      return normalizedSelection;
    }

    return String(customCancellationReason || "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleConfirmCancelOrder = async () => {
    const orderId = cancelDialogOrder?.id;
    if (!orderId) return;

    const reason = resolveCancellationReason();
    if (!reason) {
      setCancelDialogError("Please choose or enter a cancellation reason.");
      return;
    }

    setActionError("");
    setCancelDialogError("");
    setCancellingOrderId(orderId);

    try {
      const result = await cancelOrder(orderId, { reason });
      await fetchOrders(activeTab, page, appliedOrderSearchQuery);
      handleCloseCancelDialog({ force: true });
      // Target-v2 (D45): once the parcel is already with the rider, cancelling is
      // a request the seller has to answer, not something that just happened.
      // Saying "cancelled" here would be a lie the shopper acts on.
      if (result?.cancellationRequested || result?.data?.cancellationRequested) {
        await Swal.fire({
          icon: "info",
          title: "We asked the seller to stop the delivery",
          text: "Your parcel is already out with a rider, so the seller has to confirm. Your order stays active until they do.",
          confirmButtonText: "Got it",
        });
      }
    } catch (err) {
      if (err?.status === 401 || err?.statusCode === 401 || err?.status === 403 || err?.statusCode === 403) {
        logout();
        navigate("/login", {
          replace: true,
          state: { from: "/account/purchase" },
        });
        return;
      }

      const message = err?.message || "Unable to cancel this order right now.";
      setActionError(message);
      setCancelDialogError(message);
    } finally {
      setCancellingOrderId(null);
    }
  };

  const resolvedCancellationReason = resolveCancellationReason();
  const isOtherReasonSelected = selectedCancellationReason === CANCELLATION_REASON_OTHER;
  const canSubmitCancellationReason =
    Boolean(cancelDialogOrder?.id)
    && Boolean(resolvedCancellationReason)
    && resolvedCancellationReason.length <= CANCELLATION_REASON_MAX_LENGTH
    && !cancellingOrderId;
  const visibleOrders = useMemo(
    () => orders
      .map((order) => getVisibleOrderForQuery(order, appliedOrderSearchQuery))
      .filter(Boolean),
    [orders, appliedOrderSearchQuery]
  );
  const hasActiveSearch = Boolean(appliedOrderSearchQuery);
  const activeTabLabel = getTabLabel(activeTab);
  const matchingOrderCount = Number(meta?.total || 0);
  const shouldShowPagination = matchingOrderCount > PAGE_SIZE && Number(meta?.totalPages || 1) > 1;

  return (
    <div className="space-y-3">
      {actionError ? (
        <div
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200"
          role="status"
          aria-live="polite"
        >
          {actionError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div
          className="flex overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={[
                "shrink-0 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "border-neutral-900 text-neutral-900 dark:border-white dark:text-white"
                  : "border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <label
              htmlFor="my-purchase-search"
              className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400"
            >
              Search Orders
            </label>

            <div className="relative mt-2 min-w-0">
              <div className="absolute left-2.5 top-1/2 flex -translate-y-1/2 items-center">
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleSubmitOrderSearch}
                  className="rounded-full p-1 text-neutral-400 transition hover:bg-neutral-200 hover:text-neutral-600 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
                  aria-label="Submit order search"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>

              <input
                id="my-purchase-search"
                ref={orderSearchInputRef}
                type="text"
                value={orderSearchInput}
                onChange={(event) => setOrderSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSubmitOrderSearch();
                    return;
                  }

                  if (event.key === "Escape" && orderSearchInput) {
                    event.preventDefault();
                    handleClearOrderSearch();
                  }
                }}
                placeholder="Search orders"
                className="block min-h-[42px] w-full min-w-0 rounded-2xl border border-neutral-200 bg-neutral-50 py-2.5 pl-10 pr-10 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-50 dark:placeholder-neutral-500 dark:focus:border-neutral-500 dark:focus:ring-neutral-500/10"
                aria-label="Search purchases"
                role="searchbox"
                autoComplete="off"
                spellCheck="false"
              />

              {orderSearchInput ? (
                <button
                  type="button"
                  onClick={handleClearOrderSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-400 transition hover:bg-neutral-200 hover:text-neutral-600 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
                  aria-label="Clear order search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Search by order reference, seller, product, or variation. Results stay within your own orders.
            </p>

            {hasActiveSearch ? (
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
                {`Showing ${matchingOrderCount} ${matchingOrderCount === 1 ? "order" : "orders"} in ${activeTabLabel.toLowerCase()} for "${appliedOrderSearchQuery}".`}
              </p>
            ) : null}
          </div>

          {hasActiveSearch ? (
            <button
              type="button"
              onClick={handleClearOrderSearch}
              className="inline-flex shrink-0 items-center justify-center rounded-xl border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              Clear Search
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <ListSkeleton rows={4} label="Loading purchases" />
      ) : error ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-neutral-200 bg-white px-5 py-10 dark:border-neutral-800 dark:bg-neutral-900">
          <AlertCircle size={40} className="text-red-400" />
          <p className="text-center text-sm text-neutral-600 dark:text-neutral-400">
            {error}
          </p>
          <button
            type="button"
            onClick={() => fetchOrders(activeTab, page, appliedOrderSearchQuery)}
            className="rounded-xl bg-neutral-900 px-5 py-2 text-xs font-bold tracking-widest text-white transition hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
          >
            RETRY
          </button>
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-neutral-200 bg-white py-16 dark:border-neutral-800 dark:bg-neutral-900">
          <Package size={48} className="text-neutral-300 dark:text-neutral-700" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {getEmptyMessage(activeTab, appliedOrderSearchQuery)}
          </p>
          {hasActiveSearch ? (
            <button
              type="button"
              onClick={handleClearOrderSearch}
              className="rounded-xl border border-neutral-300 px-4 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              Clear search
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {refreshing ? (
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900 dark:border-neutral-700 dark:border-t-neutral-100" />
                Updating orders...
              </div>
            </div>
          ) : null}

          <div className="space-y-3">
            {visibleOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onContactSeller={handleContactSeller}
                onRateProduct={handleRateProduct}
                onTrackOrder={handleTrackOrder}
                onCancelOrder={handleRequestCancelOrder}
                onSettlePayment={handleSettlePayment}
                onRequestReturnRefund={handleRequestReturnRefund}
                cancellingOrderId={cancellingOrderId}
                settlingOrderId={settlingOrderId}
                requestingReturnRefundOrderId={requestingReturnRefundOrderId}
              />
            ))}
          </div>

          {shouldShowPagination ? (
            <div className="flex justify-center pb-1 pt-2">
              <PaginationControls
                currentPage={meta.page}
                totalPages={meta.totalPages}
                onPageChange={setPage}
                disabled={refreshing}
              />
            </div>
          ) : null}
        </>
      )}

      {returnRefundDialogOrder ? (
        <ReturnRefundRequestModal
          open
          orderLabel={returnRefundDialogOrder?.business_name || "Seller"}
          orderReference={returnRefundDialogOrder?.shopperOrderReference || returnRefundDialogOrder?.reference || null}
          returnAddress={returnRefundDialogOrder?.businessReturnAddress || returnRefundDialogOrder?.business_return_address || null}
          submitting={Number(requestingReturnRefundOrderId) === Number(returnRefundDialogOrder?.id)}
          onClose={handleCloseReturnRefundDialog}
          onSubmit={handleSubmitReturnRefund}
        />
      ) : null}

      {cancelDialogOrder ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 pb-[env(safe-area-inset-bottom)] sm:items-center sm:p-4">
          <div className="w-full rounded-t-3xl bg-white shadow-2xl dark:bg-neutral-900 sm:max-w-lg sm:rounded-3xl">
            <div className="border-b border-neutral-100 px-4 py-4 dark:border-neutral-800 sm:px-5">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                Why are you cancelling this order?
              </h2>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                This helps the seller understand what happened before we submit your cancellation.
              </p>
            </div>

            <div className="max-h-[75vh] space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
              <div className="rounded-2xl bg-neutral-50 px-3 py-2 text-xs text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300">
                Order {cancelDialogOrder.reference || `#${cancelDialogOrder.id}`}
              </div>

              <div className="space-y-2">
                {CANCELLATION_REASON_OPTIONS.map((reason) => {
                  const isSelected = selectedCancellationReason === reason;

                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => {
                        setSelectedCancellationReason(reason);
                        setCancelDialogError("");
                      }}
                      className={[
                        "flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm transition",
                        isSelected
                          ? "border-neutral-900 bg-neutral-900 text-white dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900"
                          : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-700 dark:hover:bg-neutral-800",
                      ].join(" ")}
                    >
                      <span>{reason}</span>
                      <span
                        className={[
                          "flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold",
                          isSelected
                            ? "border-current"
                            : "border-neutral-300 text-transparent dark:border-neutral-700",
                        ].join(" ")}
                      >
                        •
                      </span>
                    </button>
                  );
                })}
              </div>

              {isOtherReasonSelected ? (
                <div className="space-y-2">
                  <label
                    htmlFor="shopper-cancel-other-reason"
                    className="block text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400"
                  >
                    Tell us more
                  </label>
                  <textarea
                    id="shopper-cancel-other-reason"
                    value={customCancellationReason}
                    onChange={(event) => {
                      setCustomCancellationReason(event.target.value.slice(0, CANCELLATION_REASON_MAX_LENGTH));
                      setCancelDialogError("");
                    }}
                    rows={4}
                    maxLength={CANCELLATION_REASON_MAX_LENGTH}
                    placeholder="Enter your cancellation reason"
                    className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-neutral-600 dark:focus:ring-neutral-800"
                  />
                  <div className="text-right text-xs text-neutral-400 dark:text-neutral-500">
                    {customCancellationReason.trim().length}/{CANCELLATION_REASON_MAX_LENGTH}
                  </div>
                </div>
              ) : null}

              {cancelDialogError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                  {cancelDialogError}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-neutral-100 px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] dark:border-neutral-800 sm:flex-row sm:justify-end sm:px-5 sm:pb-4">
              <button
                type="button"
                onClick={handleCloseCancelDialog}
                disabled={Boolean(cancellingOrderId)}
                className="rounded-2xl border border-neutral-300 px-4 py-3 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                Keep Order
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelOrder}
                disabled={!canSubmitCancellationReason}
                className="rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-rose-500 dark:hover:bg-rose-400"
              >
                {cancellingOrderId ? "Submitting..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
